/**
 * Travel Comments API Service
 * 处理旅行帖子/卡片评论的增删改查操作
 */

import { supabase } from './supabase';

export interface CreateCommentPayload {
  content: string;
  target_type: 'post' | 'card';
  target_id: string;
  parent_id?: string;
}

export interface UpdateCommentPayload {
  content: string;
}

export interface TravelComment {
  id: string;
  content: string;
  author_id: string;
  target_type: 'post' | 'card';
  target_id: string;
  parent_id?: string;
  like_count: number;
  created_at: string;
  updated_at: string;
  // 扩展字段（关联查询时）
  author?: {
    id: string;
    display_name: string;
    avatar_url?: string;
    bio?: string;
  };
  replies?: TravelComment[];
  liked?: boolean;
}

/**
 * 辅助函数：为评论列表添加当前用户的点赞状态
 */
async function enrichCommentsWithLikeStatus(comments: TravelComment[]): Promise<TravelComment[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || comments.length === 0) {
    return comments.map(c => ({ ...c, liked: false }));
  }

  const commentIds = comments.map(c => c.id);
  // 分批查询以避免 URL 过长（虽然 10-20 个 ID 通常没问题）
  const { data: likes } = await supabase
    .from('travel_comment_likes')
    .select('comment_id')
    .eq('user_id', user.id)
    .in('comment_id', commentIds);

  const likedCommentIds = new Set((likes || []).map((l: any) => l.comment_id));
  return comments.map(c => ({
    ...c,
    liked: likedCommentIds.has(c.id)
  }));
}

/**
 * 创建评论
 */
export async function createComment(payload: CreateCommentPayload): Promise<TravelComment> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('用户未认证');
  }

  const { data, error } = await supabase
    .from('travel_comments')
    .insert([
      {
        content: payload.content,
        author_id: user.id,
        target_type: payload.target_type,
        target_id: payload.target_id,
        parent_id: payload.parent_id || null,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error('创建评论失败:', error);
    throw error;
  }

  return data as TravelComment;
}

/**
 * 获取单个评论
 */
export async function getComment(commentId: string): Promise<TravelComment> {
  const { data, error } = await supabase
    .from('travel_comments')
    .select(
      `
      *,
      author:author_id(id, display_name, avatar_url, bio)
      `
    )
    .eq('id', commentId)
    .single();

  if (error) {
    console.error('获取评论失败:', error);
    throw error;
  }

  return data as TravelComment;
}

/**
 * 获取帖子/卡片的评论列表（只获取顶层评论）
 */
export async function getComments(
  targetType: 'post' | 'card',
  targetId: string,
  options?: {
    page?: number;
    pageSize?: number;
    sortOrder?: 'asc' | 'desc';
  }
): Promise<{ data: TravelComment[]; count: number }> {
  let query = supabase
    .from('travel_comments')
    .select(
      `
      *,
      author:author_id(id, display_name, avatar_url, bio)
      `,
      { count: 'exact' }
    )
    .eq('target_type', targetType)
    .eq('target_id', targetId)
    .is('parent_id', null); // 只获取顶层评论

  // 排序
  const sortOrder = options?.sortOrder || 'desc';
  query = query.order('created_at', { ascending: sortOrder === 'asc' });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 10;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取评论列表失败:', error);
    throw error;
  }

  return { data: (data || []) as TravelComment[], count: count || 0 };
}

/**
 * 获取评论的回复（嵌套评论）
 */
export async function getCommentReplies(
  parentCommentId: string,
  options?: {
    page?: number;
    pageSize?: number;
  }
): Promise<{ data: TravelComment[]; count: number }> {
  let query = supabase
    .from('travel_comments')
    .select(
      `
      *,
      author:author_id(id, display_name, avatar_url, bio)
      `,
      { count: 'exact' }
    )
    .eq('parent_id', parentCommentId)
    .order('created_at', { ascending: true });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 10;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取回复失败:', error);
    throw error;
  }

  return { data: (data || []) as TravelComment[], count: count || 0 };
}

/**
 * 获取用户的评论
 */
export async function getUserComments(options?: {
  page?: number;
  pageSize?: number;
  targetType?: 'post' | 'card';
}): Promise<{ data: TravelComment[]; count: number }> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('用户未认证');
  }

  let query = supabase
    .from('travel_comments')
    .select(
      `
      *,
      author:author_id(id, display_name, avatar_url, bio)
      `,
      { count: 'exact' }
    )
    .eq('author_id', user.id);

  if (options?.targetType) {
    query = query.eq('target_type', options.targetType);
  }

  // 排序
  query = query.order('created_at', { ascending: false });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 20;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取用户评论失败:', error);
    throw error;
  }

  return { data: (data || []) as TravelComment[], count: count || 0 };
}

/**
 * 更新评论
 */
export async function updateComment(
  commentId: string,
  payload: UpdateCommentPayload
): Promise<TravelComment> {
  const { data, error } = await supabase
    .from('travel_comments')
    .update(payload)
    .eq('id', commentId)
    .select()
    .single();

  if (error) {
    console.error('更新评论失败:', error);
    throw error;
  }

  return data as TravelComment;
}

/**
 * 删除评论
 */
export async function deleteComment(commentId: string): Promise<void> {
  const { error } = await supabase
    .from('travel_comments')
    .delete()
    .eq('id', commentId);

  if (error) {
    console.error('删除评论失败:', error);
    throw error;
  }
}

/**
 * 点赞评论
 */
export async function likeComment(commentId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('用户未认证');

  const { error } = await supabase
    .from('travel_comment_likes')
    .insert({ comment_id: commentId, user_id: user.id });

  if (error) {
    // 忽略重复点赞错误
    if (error.code === '23505') return;
    console.error('点赞评论失败:', error);
    throw error;
  }
}

/**
 * 取消点赞评论
 */
export async function unlikeComment(commentId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('用户未认证');

  const { error } = await supabase
    .from('travel_comment_likes')
    .delete()
    .match({ comment_id: commentId, user_id: user.id });

  if (error) {
    console.error('取消点赞失败:', error);
    throw error;
  }
}

/**
 * 批量获取评论（带回复）
 */
export async function getCommentsWithReplies(
  targetType: 'post' | 'card',
  targetId: string,
  options?: {
    page?: number;
    pageSize?: number;
  }
): Promise<{ data: TravelComment[]; count: number }> {
  // 先获取顶层评论
  const { data: topComments, count } = await getComments(
    targetType,
    targetId,
    options
  );

  // 获取所有相关的评论 ID（包括即将获取的回复）
  // 这里我们先获取回复，然后再统一处理点赞状态
  
  const commentsWithReplies = await Promise.all(
    topComments.map(async (comment) => {
      const { data: replies } = await getCommentReplies(comment.id, {
        pageSize: 3, // 默认显示3条回复
      });
      return {
        ...comment,
        replies: replies || [],
      };
    })
  );

  // 收集所有评论 ID (顶层 + 回复)
  const allComments: TravelComment[] = [];
  commentsWithReplies.forEach(c => {
    allComments.push(c);
    if (c.replies) allComments.push(...c.replies);
  });

  // 批量获取点赞状态
  const enrichedAllComments = await enrichCommentsWithLikeStatus(allComments);
  
  // 重新组装结构
  const enrichedMap = new Map(enrichedAllComments.map(c => [c.id, c]));
  
  const finalComments = commentsWithReplies.map(c => {
    const enrichedTop = enrichedMap.get(c.id) || c;
    const enrichedReplies = (c.replies || []).map(r => enrichedMap.get(r.id) || r);
    return {
      ...enrichedTop,
      replies: enrichedReplies,
    };
  });

  return { data: finalComments, count };
}

export default {
  createComment,
  getComment,
  getComments,
  getCommentReplies,
  getUserComments,
  updateComment,
  deleteComment,
  likeComment,
  unlikeComment,
  getCommentsWithReplies,
};
